"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSubscriptionState = void 0;
const subscriptionState_1 = require("./subscriptionState");
function getSubscriptionState(subscription) {
    const now = new Date();
    const type = parseType(subscription);
    const periodEnd = subscription.ends;
    const lifecycle = parseLifecycle(subscription, now);
    const renewal = parseRenewal(subscription, now);
    const entitlement = parseEntitlement(subscription, now);
    const billing = parseBillingStatus(subscription);
    const status = parseStatus(lifecycle, renewal, billing);
    return {
        type,
        periodEnd,
        status,
        renewal,
        entitlement,
        billing,
        lifecycle,
    };
}
exports.getSubscriptionState = getSubscriptionState;
function parseType(subscription) {
    if (subscription.stripeSubscriptionId) {
        return subscriptionState_1.SubscriptionType.Stripe;
    }
    return subscriptionState_1.SubscriptionType.Manual;
}
function parseBillingStatus(subscription) {
    // stripe-managed plan
    if (subscription.stripeSubscriptionId) {
        // these states all indicate there is an overdue invoice which the user
        // needs to resolve.
        if (subscription.stripeLastStatus === "past_due" ||
            subscription.stripeLastStatus === "incomplete" ||
            subscription.stripeLastStatus === "unpaid") {
            return subscriptionState_1.BillingStatus.Unpaid;
        }
        // other states are either paid, or the subscription has been terminated.
        return subscriptionState_1.BillingStatus.Ok;
    }
    // the manual plan doesn't have billing, so it's always considered okay.
    return subscriptionState_1.BillingStatus.Ok;
}
/**
 * Is the user currently entitled to use the subscription?
 * i.e. is billing up to date
 *
 * For stripe-managed plans, we use the subscription 'status' from Stripe to
 * determine if the subscription is in good standing or not.
 * https://docs.stripe.com/billing/subscriptions/overview#subscription-statuses
 *
 * - 'active' means the current period has been paid, the subscription is in
 * good standing.
 *
 * - 'past_due' means the recurring payment failed, but Stripe is still
 * attempting payment retries. We can be in this state up to 2 weeks currently,
 * before it finally moves to the 'unpaid' state. We previously allowed a grace
 * period while in this state, but it added unnecessary complexity to the logic
 * and was removed.
 *
 * 'incomplete', 'unpaid', mean the invoice for the current period has not
 * been paid, but the user can still make payment to resume the subscription.
 * The subscription is not in good standing.
 *
 * 'incomplete_expired' and 'canceled' are terminal states, indicating the
 * subscription is terminated and cannot be resumed. The subscription is not
 * in good standing.
 *
 * 'trialing' and 'paused' are only used for trial subscriptions in Stripe.
 * Since we don't use that feature, they can be ignored.
 */
function parseEntitlement(subscription, now) {
    // stripe-managed plan
    if (subscription.stripeSubscriptionId) {
        if (subscription.stripeLastStatus === "active") {
            return subscriptionState_1.Entitlement.Entitled;
        }
        if (subscription.stripeLastStatus === "past_due") {
            return subscriptionState_1.Entitlement.Suspended;
        }
        return subscriptionState_1.Entitlement.Suspended;
    }
    // manual plan
    if (now <= subscription.ends) {
        return subscriptionState_1.Entitlement.Entitled;
    }
    return subscriptionState_1.Entitlement.Suspended;
}
function parseRenewal(subscription, now) {
    // stripe-managed plan
    if (subscription.stripeSubscriptionId) {
        // these are the only two states in Stripe which mean the subscription has
        // completely terminated.
        if (subscription.stripeLastStatus === "canceled" ||
            subscription.stripeLastStatus === "incomplete_expired") {
            return subscriptionState_1.RenewalAction.None;
        }
        // both cancelled and downgrading being true should never be possible, but
        // if it happens, we prioritise the cancel state.
        if (subscription.isCancelled) {
            return subscriptionState_1.RenewalAction.Cancel;
        }
        if (subscription.downgradeToPlan) {
            return subscriptionState_1.RenewalAction.Downgrade;
        }
        return subscriptionState_1.RenewalAction.Renew;
    }
    // manual plan has either already expired or will cancel and expire at
    // the end of the current period. Renew and Downgrade don't exist for manual.
    if (now > subscription.ends) {
        return subscriptionState_1.RenewalAction.None;
    }
    return subscriptionState_1.RenewalAction.Cancel;
}
function parseLifecycle(subscription, now) {
    // stripe-managed subscription
    if (subscription.stripeSubscriptionId) {
        // these are the only two states in Stripe which mean the subscription has
        // completely terminated.
        if (subscription.stripeLastStatus === "canceled" ||
            subscription.stripeLastStatus === "incomplete_expired") {
            return subscriptionState_1.Lifecycle.Terminated;
        }
        return subscriptionState_1.Lifecycle.Active;
    }
    // manual subscription
    if (now > subscription.ends) {
        return subscriptionState_1.Lifecycle.Terminated;
    }
    return subscriptionState_1.Lifecycle.Active;
}
function parseStatus(lifecycle, renewal, billing) {
    if (lifecycle === subscriptionState_1.Lifecycle.Terminated) {
        return subscriptionState_1.Status.Expired;
    }
    if (billing === subscriptionState_1.BillingStatus.Unpaid) {
        return subscriptionState_1.Status.RequiresAction;
    }
    if (renewal === subscriptionState_1.RenewalAction.Cancel) {
        return subscriptionState_1.Status.Cancelled;
    }
    if (renewal === subscriptionState_1.RenewalAction.Downgrade) {
        return subscriptionState_1.Status.DowngradeScheduled;
    }
    return subscriptionState_1.Status.Active;
}
